import { computed, ref } from "vue";
import { initDateLibs } from "../utils/initDateLibs";
import { GlobalSettings } from "../types/globalSettings";
import { rubyDateTimeFormatToJsFormat } from "../utils/date";
import { LoadingStatus } from "../enums/general";
import { notifyAPIError } from "@/src/shared/api/utils";
import { FILE_SIZE_MULTIPLIER } from "@/src/shared_modules/attachments/constants/attachments";

export class EasySettings {
  globalSettings = ref(<GlobalSettings>{});
  globalSettingsLoading = ref<LoadingStatus>(LoadingStatus.Init);

  getGlobalDateFormat = computed(
    () => rubyDateTimeFormatToJsFormat(this.globalSettings.value.date_format) || "D. M. YYYY"
  );

  getGlobalTimeFormat = computed<string>(
    () => rubyDateTimeFormatToJsFormat(this.globalSettings.value.time_format) || "HH:mm"
  );

  getGlobalDateTimeFormat = computed<string>(
    () => `${this.getGlobalDateFormat.value} ${this.getGlobalTimeFormat.value}`
  );

  getStartOfWeek = computed(() => {
    if (!this.globalSettings.value.start_of_week) return -1;

    return Number(this.globalSettings.value.start_of_week);
  });

  getTimelogCommentEditorEnabled = computed(() => {
    return !!this.globalSettings.value.timelog_comment_editor_enabled;
  });

  getTextFormatting = computed(() => {
    return this.globalSettings.value.text_formatting;
  });

  getTextile = computed(() => {
    return !!this.getTextFormatting.value && this.getTextFormatting.value !== "HTML";
  });

  getAttachmentMaxSizeB = computed(() => {
    return this.globalSettings.value.attachment_max_size
      ? parseInt(this.globalSettings.value.attachment_max_size) * FILE_SIZE_MULTIPLIER
      : null;
  });

  getEnablePrivateIssues = computed(() => {
    return this.globalSettings.value.enable_private_issues;
  });

  getBillableThingsDefaultState = computed(() => {
    return this.globalSettings.value.billable_things_default_state;
  });

  getIssueDoneRatio = computed(() => {
    return this.globalSettings.value.issue_done_ratio;
  });

  getShowBillableThings = computed(() => {
    return !!this.globalSettings.value.billable_field_enabled;
  });

  getEnableActivityRoles = computed(() => {
    return !!this.globalSettings.value.enable_activity_roles;
  });

  setGlobalSettings(settings: GlobalSettings): void {
    this.globalSettings.value = settings;
  }

  async init(settings: GlobalSettings) {
    if (this.globalSettingsLoading.value !== LoadingStatus.Init) return;

    this.globalSettingsLoading.value = LoadingStatus.Loading;

    try {
      await this.initGlobalSettings(settings);
      this.globalSettingsLoading.value = LoadingStatus.Loaded;
    } catch (err) {
      notifyAPIError(err);
      this.globalSettingsLoading.value = LoadingStatus.Init;
    }
  }

  async initGlobalSettings(settings: GlobalSettings) {
    this.setGlobalSettings(settings);
    await this.initGlobalDateSettings();
  }

  async initGlobalDateSettings() {
    await initDateLibs(window.EASY.currentUser.language, this.getStartOfWeek.value);
  }
}

export const easySettings = new EasySettings();
